import React, { useEffect } from "react";
import { MsalProvider } from "@azure/msal-react";
import { useLocation } from "react-router";
import { extractLangcodeFromDomain, setAppInternationalization } from "./utils/Internationalization/handleAppInternationalization";
import "./App.css";
import { nativeAppRoutePaths, routePrefix } from "./constants/Routes";
import { ENVSettings } from "./constants/ENVSettings";
import { getLocalSessionData } from "./utils/Authentication/handleUserSession";
import { LOCALSESSIONDATAKEYS } from "./constants/AppConstants";
import { useQuery } from "./utils/CustomHooks/UseQuery";
import { languageCodes } from "./constants/Country";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import AppRoutes from "./routes/AppRoutes";
import { isNativeAppPage } from "./constants/AppMethod";
import Loader from "./components/shared/loader/Loader";
import RouteListener from "./utils/RouteListener/RouteListener.js";
import useNativeAppQueryParamsToLocalStorage from "./utils/CustomHooks/useNativeAppQueryParamsToLocalStorage.js";

const gtmTrackingId = ENVSettings.GTM_TRACKING_ID

const App = ({ msalInstance }) => {
  const { t } = useTranslation(["translation"]);
  const location = useLocation();
  const { ready } = useNativeAppQueryParamsToLocalStorage();
  const queryParams = useQuery();
  const language= queryParams.get("language");
  const country= queryParams.get("country");
  const domain = window.location.href;
  const domainLangCode = extractLangcodeFromDomain();
  let languageCode;

  if (domain.includes('?') && language && country){
    languageCode =  `${language}-${domainLangCode.toUpperCase()}`
  } else {
    languageCode = languageCodes[domainLangCode];
  }

  document.documentElement.setAttribute("lang", languageCode);

  useEffect(() => {
    const loadGTM = async () => {
      try {
        const TagManager = await import("react-gtm-module");
        TagManager.default.initialize({ gtmId: gtmTrackingId }); 
      } catch (error) {
        console.error("Failed to load GTM:", error);
      }
    };
    if ("requestIdleCallback" in window) {
      requestIdleCallback(loadGTM);
    } else {
      setTimeout(loadGTM, 0);
    }
  }, []);
  
  useEffect(() => {
    const isNativeAppRoute = isNativeAppPage(location?.pathname);
    window.scrollTo(0,0);

    const isWebCustomer =
    location?.pathname.startsWith(`/${routePrefix.WEB_CUSTOMER}`) &&
    !location?.pathname.startsWith(`/${routePrefix.WEB_HARDWARE}`);

    if (isNativeAppRoute || isWebCustomer) {
      if(location?.pathname.indexOf(nativeAppRoutePaths.ONE_BENEFIT_PAGE_URL) > -1)
      {
        document.body.classList.add("bos-background");
      } else {
        document.body.classList.add("teaser-background");
      }
    }
    else{
      document.body.classList.remove("teaser-background");
    }
    
    const ECOPROLNGCODESTATUS = getLocalSessionData(LOCALSESSIONDATAKEYS.ECOPROLNGCODESTATUS);
    if(!ECOPROLNGCODESTATUS) {
      setAppInternationalization();
    }
  }, [location.pathname]);

  return (
    <MsalProvider instance={msalInstance}>
      <Helmet>
          <title>{t("PAGE_TITLE.DEFAULT_TITLE")}</title>
          <meta name="description" content={t("PAGE_TITLE.DEFAULT_DESCRIPTION")} />
      </Helmet>
      <div id="backdrop">
          <div className="app_routes">
            {
              (ready)
              ? <>
                  <RouteListener />
                  <AppRoutes/> 
                </>
              : <Loader />
            }
          </div>
      </div>
    </MsalProvider>
  );
  }

export default App;
